import React from 'react'
import { Icon as CheckmarkIcon } from '@enamelai/design-system/icons/Checkmark2'
import { Icon as InfoIcon } from '@enamelai/design-system/icons/Info'
import { Icon as AlertIcon } from '@enamelai/design-system/icons/Alert'
import { Icon as ClockIcon } from '@enamelai/design-system/icons/Clock'
import { Icon as RefreshIcon } from '@enamelai/design-system/icons/Refresh_CCW'
import type { StreamingVoicePhase } from '@hooks/useStreamingVoiceSession'
import type {
  VoiceTranscriptEntry,
  VoiceTranscriptStatus,
} from '@hooks/useVoiceTranscriptFeed'

export interface VoicePromptGroup {
  label: string
  prompts: string[]
}

export interface ChartingVoiceStatusProps {
  phase: StreamingVoicePhase
  backlog: number
  message: string
  history: VoiceTranscriptEntry[]
  liveText: string
  queued: string[]
  paused: boolean
  ended?: boolean
  ariaLabel: string
  className?: string
  promptGroupsClassName?: string
  stateExtra?: React.ReactNode
  midContent?: React.ReactNode
  quickPrompts: string[]
  promptGroups: VoicePromptGroup[]
  tip: string
  renderHistoryText?: (text: string) => React.ReactNode
}

// Voice-log text is selectable while the local dev server is running.
const allowVoiceLogSelect = process.env.NODE_ENV === 'development'

const VOICE_LOG_ICON_SIZE = 13

const STATUS_DISPLAY: Record<
  VoiceTranscriptStatus,
  { Icon: React.FC<React.SVGProps<SVGSVGElement>>; label: string }
> = {
  pending: { Icon: RefreshIcon, label: 'Processing' },
  applied: { Icon: CheckmarkIcon, label: 'Applied' },
  info: { Icon: InfoIcon, label: 'Heard' },
  failed: { Icon: AlertIcon, label: 'Not applied' },
}

const emptyLogMessage = (
  phase: StreamingVoicePhase,
  paused: boolean,
): string => {
  if (phase === 'connecting') return 'Connecting…'
  if (phase === 'listening' && !paused) return 'Listening — start speaking.'
  return 'Transcripts will appear here.'
}

const ChartingVoiceStatus = ({
  phase,
  backlog,
  message,
  history,
  liveText,
  queued,
  paused,
  ended = false,
  ariaLabel,
  className,
  promptGroupsClassName,
  stateExtra,
  midContent,
  quickPrompts,
  promptGroups,
  tip,
  renderHistoryText,
}: ChartingVoiceStatusProps): React.ReactElement => {
  const active = ['connecting', 'listening', 'processing'].includes(phase)
  const hasError = phase === 'error'
  const status = hasError
    ? 'Voice error'
    : phase === 'connecting'
      ? 'Connecting…'
      : paused
        ? 'Voice paused'
        : phase === 'processing' || backlog > 0
          ? 'Processing…'
          : active
            ? 'Listening'
            : 'Microphone off'
  const messageInLog = history.some((entry) => entry.note === message)
  const messageText =
    (messageInLog ? '' : message) ||
    (paused
      ? 'Say “resume” to continue.'
      : ended
        ? 'End of sequence. Choose a tooth to continue.'
        : '')
  const showMessage = Boolean(messageText)
  const isEmpty = history.length === 0 && queued.length === 0 && !liveText
  const showPulse = (phase === 'connecting' || phase === 'listening') && !paused
  const recentHistory = [...history].reverse()
  const recentQueued = [...queued].reverse()
  const sectionClass = ['perio-voice-status', className]
    .filter(Boolean)
    .join(' ')
  const groupsClass = ['perio-voice-prompt-groups', promptGroupsClassName]
    .filter(Boolean)
    .join(' ')
  const renderHistoryLine = (text: string): React.ReactNode =>
    renderHistoryText ? renderHistoryText(text) : text

  return (
    <section className={sectionClass} aria-label={ariaLabel}>
      <div
        className={`perio-voice-state${hasError ? ' text-danger' : paused ? ' perio-voice-pending' : ''}`}
        role="status"
        aria-live="polite"
      >
        <strong>{status}</strong>
        {backlog > 0 && <span>{backlog} queued</span>}
        {stateExtra}
      </div>
      <div
        className={`voice-log${allowVoiceLogSelect ? ' allow-user-select' : ''}`}
        data-testid="voice-log"
      >
        {isEmpty ? (
          <div className="voice-log-empty">
            {showPulse && (
              <span className="voice-log-pulse" aria-hidden="true" />
            )}
            {emptyLogMessage(phase, paused)}
          </div>
        ) : (
          <>
            {liveText && (
              <div className="voice-log-live" aria-live="off">
                {liveText}
                <span className="voice-log-caret" aria-hidden="true" />
              </div>
            )}
            <ol
              className="voice-log-list"
              role="log"
              aria-live="polite"
              aria-label="Recent voice transcripts"
            >
              {recentQueued.map((text, index) => (
                <li
                  key={`queued-${index}`}
                  className="voice-log-entry voice-log-queued"
                >
                  <ClockIcon
                    className="voice-log-icon"
                    width={VOICE_LOG_ICON_SIZE}
                    height={VOICE_LOG_ICON_SIZE}
                    aria-hidden
                  />
                  <span className="visually-hidden">Queued: </span>
                  <span className="voice-log-text">{text}</span>
                </li>
              ))}
              {recentHistory.map((entry) => {
                const display = STATUS_DISPLAY[entry.status]
                return (
                  <li
                    key={entry.id}
                    className={`voice-log-entry voice-log-${entry.status}`}
                  >
                    <display.Icon
                      className="voice-log-icon"
                      width={VOICE_LOG_ICON_SIZE}
                      height={VOICE_LOG_ICON_SIZE}
                      aria-hidden
                    />
                    <span className="visually-hidden">{display.label}: </span>
                    <div className="voice-log-body">
                      <span className="voice-log-text">
                        {entry.text ? (
                          renderHistoryLine(entry.text)
                        ) : (
                          <em>Unclear speech</em>
                        )}
                      </span>
                      {entry.heard && (
                        <span className="voice-log-heard">
                          Heard: “{entry.heard}”
                        </span>
                      )}
                      {entry.note && (
                        <span className="voice-log-note">{entry.note}</span>
                      )}
                    </div>
                  </li>
                )
              })}
            </ol>
          </>
        )}
      </div>
      {midContent}
      {showMessage && (
        <div
          className={`perio-voice-message${hasError ? ' text-danger' : paused ? ' perio-voice-pending' : ''}`}
          role={hasError ? 'alert' : 'status'}
        >
          {messageText}
        </div>
      )}
      <div className="perio-voice-prompts">
        <span className="perio-voice-transcript-label">Try saying</span>
        <ul
          className="perio-voice-prompt-chips"
          aria-label="Sample voice prompts"
        >
          {quickPrompts.map((prompt) => (
            <li key={prompt}>
              <span className="perio-voice-prompt-chip">{prompt}</span>
            </li>
          ))}
        </ul>
        <details className="perio-voice-prompts-more">
          <summary>More commands</summary>
          <div className={groupsClass}>
            {promptGroups.map((group) => (
              <div key={group.label} className="perio-voice-prompt-group">
                <span className="perio-voice-prompt-group-label">
                  {group.label}
                </span>
                <ul>
                  {group.prompts.map((prompt) => (
                    <li key={prompt}>
                      <span className="perio-voice-prompt-example">
                        “{prompt}”
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="perio-voice-prompts-tip">{tip}</p>
        </details>
      </div>
    </section>
  )
}

export default ChartingVoiceStatus
